import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, KeyRound, CheckCircle2 } from 'lucide-react';

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: newPassword });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || 'No se pudo restablecer la contraseña');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA] p-4" data-testid="reset-password-page">
      <div className="w-full max-w-md animate-fade-in-up">
        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-[#F72585]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <KeyRound className="w-7 h-7 text-[#F72585]" />
          </div>
          <h1 className="text-2xl font-bold text-[#0F172A] tracking-tight">Restablece tu contraseña</h1>
        </div>

        <div className="bg-white border border-[#E2E8F0] rounded-xl p-6">
          {done ? (
            <div className="text-center" data-testid="reset-password-done">
              <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
              <p className="text-sm text-[#0F172A] mb-6">Contraseña actualizada. Ya puedes iniciar sesión.</p>
              <Button onClick={() => navigate('/login')} className="w-full bg-[#0F172A] text-white hover:bg-[#1E293B]">
                Ir a iniciar sesión
              </Button>
            </div>
          ) : (
            <>
              {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Nueva contraseña</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                    <Input
                      type="password"
                      data-testid="reset-password-new-input"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Mínimo 8 caracteres"
                      minLength={8}
                      required
                      className="pl-10 h-11 bg-white border-[#E2E8F0] rounded-lg text-sm focus:border-[#0F172A] focus:ring-1 focus:ring-[#0F172A]"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Confirma tu contraseña</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                    <Input
                      type="password"
                      data-testid="reset-password-confirm-input"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Repite la contraseña"
                      minLength={8}
                      required
                      className="pl-10 h-11 bg-white border-[#E2E8F0] rounded-lg text-sm focus:border-[#0F172A] focus:ring-1 focus:ring-[#0F172A]"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  data-testid="reset-password-submit-button"
                  disabled={submitting}
                  className="w-full h-11 bg-[#0F172A] text-white hover:bg-[#1E293B] rounded-lg font-medium text-sm transition-all"
                >
                  {submitting ? 'Actualizando...' : 'Restablecer contraseña'}
                </Button>
              </form>
              <div className="mt-6 text-center">
                <Link to="/forgot-password" className="text-sm text-[#64748B] hover:text-[#0F172A] transition-colors">
                  Solicitar un nuevo enlace
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
